import { useEffect, useRef, useState, useMemo } from "react";
import { Circle, Ellipse, Text, Group, Rect } from "react-konva";
import { Html } from "react-konva-utils";
import Konva from "konva";
import "../styles.css";
import { applyTransparency } from "../../../SectionElements/plugins/utils";
import {
  checkSimpleHitDetection,
  checkAdvancedHitDetection,
} from "../../../SectionElements/utils/pixelHitDetection";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import {
  getAbsoluteHorizontalExtent,
  getOffCanvasMainColumnBounds,
  getOffCanvasHorizontalStripSide,
} from "../../utils/boundaryUtils";
import { constrainOffCanvasPosition } from "../../utils/dragEndUtils";
import { getOffCanvasStageWidthPx } from "../../utils/mainCanvasLayout";

const CircleElement = ({
  circle,
  strokeColor,
  isDraggable = true,
  onClick,
  onDragEnd,
  onTransformEnd,
  onTransform,
  onDragMove,
  canvasHeight,
  canvasWidth = window.innerWidth * 0.45,
  canvasResizeTrigger = 0,
  images = [], // Array of images for pixel-level hit detection
  stageRef = null, // Stage reference for hit detection
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
  onImageClick = null, // Callback for when an underlying image should be selected
  effectiveZoomLevel = 1, // Zoom level for scaling elements
  isSelected = false,
  isOffCanvas = false,
  isPreview = false,
  onCopyToOppositeCanvas,
  selectedObject = null,
  selectedObjects = [],
}) => {
  const shapeRef = useRef();
  const textGroupRef = useRef();
  const positionRef = useRef({ x: circle.x, y: circle.y });
  const isAspectRatioChangingRef = useRef(false);
  const justDoubleClickedRef = useRef(false);
  const doubleClickOriginalPositionRef = useRef({ x: null, y: null });
  const offCanvasDragStripSideRef = useRef(null);
  const transformStartCenterRef = useRef(null);

  const MIN_WIDTH = 10;
  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = 600;
  const allowDrag = isDraggable && !circle.isNew;
  const isDrawing = circle.isNew;
  const PADDING = 4;
  const TRANSFORMER_ANCHOR_BUFFER_PX = 10;

  // Add back the radius constants that were accidentally removed
  const MIN_RADIUS = 5;

  // For oval support when maintainAspectRatio is false
  const maintainAspectRatio = circle.maintainAspectRatio ?? true;

  // State to force re-render when canvas is resized
  const [renderKey, setRenderKey] = useState(0);

  // Force re-render when canvas is resized
  useEffect(() => {
    // Force a re-render to recalculate text dimensions and clipping
    setRenderKey((prev) => prev + 1);
  }, [canvasResizeTrigger, canvasHeight]);

  // Track if we're in a group drag to prevent HTML re-renders
  const isInGroupDragRef = useRef(false);

  // CRITICAL PERFORMANCE FIX: Cache HTML content during group drag to prevent expensive re-renders
  // The HTML content is expensive to render, especially with lots of formatting
  // During group drag, we only update Konva node positions, not the HTML content
  // Use circle.text instead of textContent since textContent is defined later
  const htmlContentRef = useRef(circle.text || "");

  // Update shape and text Group position during group drag using requestAnimationFrame for smooth movement
  // CRITICAL PERFORMANCE FIX: This effect only updates Konva node positions, NOT React state or HTML content
  // This prevents expensive HTML re-renders during drag move
  useEffect(() => {
    const isInGroupDrag = selectedObjects && selectedObjects.length > 1 &&
      selectedObjects.some((obj) => obj.id === circle.id && obj.type === "Circle");

    // Update ref to track group drag state (used to prevent HTML re-renders)
    isInGroupDragRef.current = isInGroupDrag;

    // Cache HTML content when entering group drag
    // Use circle.text instead of textContent since textContent is defined later
    if (isInGroupDrag && htmlContentRef.current !== circle.text) {
      htmlContentRef.current = circle.text || "";
    }

    if (!isInGroupDrag || !textGroupRef.current || !shapeRef.current) {
      return;
    }

    let animationFrameId;
    let lastX = null;
    let lastY = null;
    let lastRotation = null;

    const updatePositions = () => {
      if (textGroupRef.current && shapeRef.current) {
        try {
          // Get current position from shape node (updated by handleGroupDragMove)
          const currentX = shapeRef.current.x();
          const currentY = shapeRef.current.y();
          const currentRotation = shapeRef.current.rotation();

          // Only update if position changed (avoid unnecessary updates)
          if (currentX !== lastX || currentY !== lastY || currentRotation !== lastRotation) {
            lastX = currentX;
            lastY = currentY;
            lastRotation = currentRotation;

            // Recalculate text dimensions (same logic as getTextContainerDimensions)
            const baseStrokeWidth = circle.strokeWidth || 2;
            const effectiveStrokeWidth = Math.max(
              1,
              baseStrokeWidth / Math.max(0.3, effectiveZoomLevel || 1)
            );
            const strokePadding = Math.ceil(effectiveStrokeWidth / 2);

            // Calculate actualRadiusX and actualRadiusY (same logic as component)
            const radius = circle.radius;
            const radiusX = circle.radiusX || radius;
            const radiusY = circle.radiusY || radius;
            const actualRadiusX = maintainAspectRatio ? radius : radiusX;
            const actualRadiusY = maintainAspectRatio ? radius : radiusY;

            let currentRadiusX = actualRadiusX;
            let currentRadiusY = actualRadiusY;
            if (shapeRef.current) {
              try {
                const scaleX = shapeRef.current.scaleX ? shapeRef.current.scaleX() : 1;
                const scaleY = shapeRef.current.scaleY ? shapeRef.current.scaleY() : 1;
                if (maintainAspectRatio) {
                  const nodeRadius = typeof shapeRef.current.radius === "function"
                    ? shapeRef.current.radius()
                    : actualRadiusX;
                  currentRadiusX = nodeRadius * scaleX;
                  currentRadiusY = nodeRadius * scaleY;
                } else {
                  const nodeRadiusX = typeof shapeRef.current.radiusX === "function"
                    ? shapeRef.current.radiusX()
                    : actualRadiusX;
                  const nodeRadiusY = typeof shapeRef.current.radiusY === "function"
                    ? shapeRef.current.radiusY()
                    : actualRadiusY;
                  currentRadiusX = nodeRadiusX * scaleX;
                  currentRadiusY = nodeRadiusY * scaleY;
                }
              } catch (e) {
                // Fallback
              }
            }

            const availableWidth = Math.max(0, currentRadiusX * 2 - strokePadding * 2);
            const availableHeight = Math.max(0, currentRadiusY * 2 - strokePadding * 2);
            const availableSize = Math.min(availableWidth, availableHeight);
            const fontSize = circle.fontSize || 15;
            const minPadding = Math.max(6, fontSize * 0.2);
            const paddingFromStroke = Math.max(minPadding, strokePadding * 1.5);
            const containerSize = Math.max(0, availableSize * 0.7 - paddingFromStroke);

            // CRITICAL FIX: Update both Konva Group position AND HTML container position for smooth text movement
            // This prevents text lag during drag by keeping HTML in sync with shape
            textGroupRef.current.x(currentX);
            textGroupRef.current.y(currentY);
            textGroupRef.current.offsetX(containerSize / 2);
            textGroupRef.current.offsetY(containerSize / 2);
            textGroupRef.current.rotation(currentRotation);

            // CRITICAL: Update HTML container position directly for smooth text movement
            // This ensures the HTML text follows the shape instantly during drag
            const htmlContainer = textGroupRef.current.getLayer()?.canvas?.container?.querySelector('.konvajs-content');
            if (htmlContainer) {
              const htmlElements = htmlContainer.querySelectorAll('[data-circle-id="' + circle.id + '"]');
              htmlElements.forEach(htmlEl => {
                if (htmlEl && htmlEl.style) {
                  // Get stage position to convert coordinates
                  const stage = shapeRef.current.getStage();
                  if (stage) {
                    const stageContainer = stage.container();
                    const stageRect = stageContainer.getBoundingClientRect();

                    // Calculate absolute position for HTML element (centered on circle)
                    const absoluteX = stageRect.left + currentX;
                    const absoluteY = stageRect.top + currentY;

                    htmlEl.style.transform = `translate(${absoluteX}px, ${absoluteY}px) rotate(${currentRotation}deg)`;
                    htmlEl.style.position = 'fixed';
                    htmlEl.style.zIndex = isSelected ? '15' : '3';
                  }
                }
              });
            }

            // Force layer redraw for smooth updates
            const layer = textGroupRef.current.getLayer();
            if (layer) {
              layer.batchDraw();
            }

            // Also ensure shape layer is drawn (for stroke updates)
            const shapeLayer = shapeRef.current.getLayer();
            if (shapeLayer && shapeLayer !== layer) {
              shapeLayer.batchDraw();
            }
          }
        } catch (e) {
          // Ignore errors during drag
        }
      }
      animationFrameId = requestAnimationFrame(updatePositions);
    };

    animationFrameId = requestAnimationFrame(updatePositions);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      // Reset ref when effect cleans up
      isInGroupDragRef.current = false;
    };
  }, [selectedObjects, circle.id, circle.radius, circle.radiusX, circle.radiusY, circle.strokeWidth, circle.fontSize, effectiveZoomLevel, maintainAspectRatio, circle.text]);

  // Calculate canvas boundaries for clipping (recalculated on each render)
  const canvasPixelHeight =
    canvasHeight || (parseInt(canvasHeight) / 100) * window.innerHeight;
  const canvasPixelWidth = isOffCanvas
    ? canvasWidth || getOffCanvasStageWidthPx()
    : canvasWidth || window.innerWidth * 0.45;

  // Resize handle constants (must match Canvas.jsx)
  const RESIZE_HANDLE_HEIGHT = 10;
  const RESIZE_BOTTOM_SAFE = 0;
  // Effective canvas height for text clipping (subtract resize handle area)
  const effectiveCanvasHeight = Math.max(
    0,
    canvasPixelHeight - RESIZE_HANDLE_HEIGHT - RESIZE_BOTTOM_SAFE
  );

  const MAX_WIDTH = Math.max(
    MIN_WIDTH,
    Number(canvasPixelWidth) || window.innerWidth * 0.45,
  );
  const computedMaxRadius = Math.max(
    MIN_RADIUS,
    Math.min(MAX_WIDTH / 2, Math.max(MIN_RADIUS, effectiveCanvasHeight / 2)),
  );
  // Hard ceiling requested: don't allow circle/ellipse radius > 350.
  const MAX_RADIUS = Math.min(computedMaxRadius, 350);

  const {
    text: textContent = "",
    fontSize = 15,
    textColor = "black",
    fontStyle = "normal",
    textDecoration = "",
    align = "center",
    verticalAlign = "middle",
  } = circle;

  const parsedFontStyle = (() => {
    const isItalic = fontStyle.includes("italic");
    const isBold = fontStyle.includes("bold");

    if (isItalic && isBold) return "italic bold";
    if (isItalic) return "italic";
    if (isBold) return "bold";
    return "normal";
  })();

  // Function to parse HTML-like formatting and apply highlighting
  const parseFormattedText = (text) => {
    if (!text) return text;

    // Remove HTML-like formatting for display
    let cleanText = text
      .replace(/<bold>(.*?)<\/bold>/g, "$1") // Remove bold tags
      .replace(/<italic>(.*?)<\/italic>/g, "$1") // Remove italic tags
      .replace(/<underline>(.*?)<\/underline>/g, "$1") // Remove underline tags
      .replace(/<highlight>(.*?)<\/highlight>/g, "$1") // Remove highlight tags
      .replace(/<color[^>]*>(.*?)<\/color>/g, "$1") // Remove color tags
      .replace(/<fontsize[^>]*>(.*?)<\/fontsize>/g, "$1"); // Remove font size tags

    return cleanText;
  };

  // Function to render text with highlighting
  const renderTextWithHighlighting = (text) => {
    if (!text) return text;

    // Split text by highlight tags and create spans
    const parts = text.split(/(<highlight>(.*?)<\/highlight>)/g);
    return parts.map((part, index) => {
      if (part.startsWith("<highlight>") && part.endsWith("</highlight>")) {
        // This is highlighted text
        const highlightedText = part.slice(10, -11); // Remove <highlight> and </highlight>
        return (
          <span
            key={index}
            style={{ backgroundColor: "#ffff00", color: "#000" }}
          >
            {highlightedText}
          </span>
        );
      }
      return part;
    });
  };

  const displayText = parseFormattedText(textContent);

  // For oval support, we use radiusX and radiusY (like radius for circle)
  const radius = circle.radius;
  const radiusX = circle.radiusX || radius; // Default to radius if radiusX not set
  const radiusY = circle.radiusY || radius; // Default to radius if radiusY not set

  // Calculate dimensions based on maintainAspectRatio
  const actualRadiusX = maintainAspectRatio ? radius : radiusX;
  const actualRadiusY = maintainAspectRatio ? radius : radiusY;

  const [textSize, setTextSize] = useState({ width: 0, height: 0 });

  // Check if this element's HTML content is behind any selected element
  const isHtmlContentBehindSelected = () => {
    // If this element is selected, don't hide its HTML content
    if (isSelected) {
      return false;
    }

    // Get all selected elements (from selectedObjects or selectedObject)
    const allSelected = [];
    if (selectedObjects && selectedObjects.length > 0) {
      allSelected.push(...selectedObjects);
    } else if (selectedObject) {
      allSelected.push(selectedObject);
    }

    if (allSelected.length === 0) {
      return false;
    }

    // Get this element's bounding box
    const radius = actualRadiusX;
    const thisBBox = {
      x: circle.x - radius,
      y: circle.y - radius,
      width: radius * 2,
      height: radius * 2,
    };

    // Check if any selected element overlaps with this element
    return allSelected.some((selected) => {
      // Skip if it's the same element
      if (selected.id === circle.id) {
        return false;
      }

      // Get selected element's bounding box
      let selectedBBox = null;
      if (selected.type === "Rect") {
        selectedBBox = {
          x: selected.x || 0,
          y: selected.y || 0,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      } else if (selected.type === "Circle") {
        const selectedRadius =
          selected.radius ||
          Math.max(selected.radiusX || 0, selected.radiusY || 0);
        selectedBBox = {
          x: (selected.x || 0) - selectedRadius,
          y: (selected.y || 0) - selectedRadius,
          width: selectedRadius * 2,
          height: selectedRadius * 2,
        };
      } else if (selected.type === "Text" || selected.type?.includes("Text")) {
        selectedBBox = {
          x: selected.x || 0,
          y: selected.y || 0,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      } else if (selected.type === "Image") {
        selectedBBox = {
          x: (selected.x || 0) - (selected.width || 0) / 2,
          y: (selected.y || 0) - (selected.height || 0) / 2,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      }

      if (!selectedBBox) {
        return false;
      }

      // Check for overlap (simple rectangle overlap check)
      const hasOverlap = !(
        thisBBox.x + thisBBox.width < selectedBBox.x ||
        thisBBox.x > selectedBBox.x + selectedBBox.width ||
        thisBBox.y + thisBBox.height < selectedBBox.y ||
        thisBBox.y > selectedBBox.y + selectedBBox.height
      );

      return hasOverlap;
    });
  };

  // Get mask style to hide only the overlapping portion
  // Returns an object with mask-image properties (not used, kept for compatibility)
  // The actual clipping is done via clip-path on the inner div
  const getHtmlMask = (divWidth = null, divHeight = null) => {
    // Don't apply any mask here - we use clip-path on the inner div instead
    // This prevents double application of clipping
    return { maskImage: "none", WebkitMaskImage: "none" };
  };

  // Calculate clip-path to hide only the overlapping portion (kept for backward compatibility)
  const getHtmlClipPath = () => {
    return "none"; // We use mask instead of clip-path
  };

  // Get visibility style for HTML content (for backward compatibility, but we'll use clip-path instead)
  const getHtmlVisibility = () => {
    // Don't use visibility anymore - use clip-path instead
    return "visible";
  };

  // Get opacity for HTML content - reduce opacity when another element is selected
  const getHtmlOpacity = () => {
    // Check if this element is in selectedObjects array
    const isInSelectedObjects = selectedObjects && selectedObjects.some(
      (obj) => obj.id === circle.id && obj.type === "Circle"
    );

    // If this element is selected (either via isSelected prop or in selectedObjects), keep full opacity
    if (isSelected || isInSelectedObjects) {
      return circle.opacity !== undefined ? circle.opacity : 1;
    }

    // Get all selected elements (from selectedObjects or selectedObject)
    const allSelected = [];
    if (selectedObjects && selectedObjects.length > 0) {
      allSelected.push(...selectedObjects);
    } else if (selectedObject) {
      allSelected.push(selectedObject);
    }

    // If nothing is selected, keep full opacity
    if (allSelected.length === 0) {
      return circle.opacity !== undefined ? circle.opacity : 1;
    }

    // Otherwise, reduce opacity for non-selected elements when something else is selected
    const baseOpacity = circle.opacity !== undefined ? circle.opacity : 1;
    return baseOpacity * 0.3;
  };

  // Check if circle/oval element is completely outside canvas boundaries
  const isOutsideCanvas = () => {
    const elementRight = circle.x + actualRadiusX * 2;
    const elementBottom = circle.y + actualRadiusY * 2;

    return (
      circle.x >= canvasPixelWidth || // Completely to the right
      circle.y >= canvasPixelHeight || // Completely below
      elementRight <= 0 || // Completely to the left
      elementBottom <= 0 // Completely above
    );
  };

  // Update position ref when circle position changes
  useEffect(() => {
    positionRef.current = { x: circle.x, y: circle.y };
  }, [circle.x, circle.y]);

  // Ensure position is maintained when maintainAspectRatio changes
  useEffect(() => {
    isAspectRatioChangingRef.current = true;

    if (shapeRef.current) {
      // Force the element to stay at its original position
      const preservedPosition = positionRef.current;
      shapeRef.current.x(preservedPosition.x);
      shapeRef.current.y(preservedPosition.y);

      // Force a redraw to ensure the position is applied
      const layer = shapeRef.current.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      isAspectRatioChangingRef.current = false;
    }, 100);
  }, [maintainAspectRatio]);

  // Additional effect to continuously maintain position
  useEffect(() => {
    if (shapeRef.current && !isAspectRatioChangingRef.current) {
      // Ensure position matches the circle object exactly
      if (
        shapeRef.current.x() !== circle.x ||
        shapeRef.current.y() !== circle.y
      ) {
        shapeRef.current.x(circle.x);
        shapeRef.current.y(circle.y);

        // Force a redraw
        const layer = shapeRef.current.getLayer();
        if (layer) {
          layer.batchDraw();
        }
      }
    }
  }, [circle.x, circle.y, maintainAspectRatio]);

  // Calculate text dimensions for sizing (similar to RectangleElement approach)
  useEffect(() => {
    if (!textContent) return;

    const textDimensions = getTextContainerDimensions();

    // Function to extract effective font size and clean text
    const getEffectiveFontSizeAndCleanText = (text) => {
      let effectiveFontSize = fontSize;
      let cleanText = text;

      // Extract font size from fontsize tags
      const fontSizeMatch = text.match(
        /<fontsize[^>]*style="font-size:\s*(\d+)px"[^>]*>(.*?)<\/fontsize>/g
      );
      if (fontSizeMatch) {
        // Use the largest font size found
        fontSizeMatch.forEach((match) => {
          const sizeMatch = match.match(/font-size:\s*(\d+)px/);
          if (sizeMatch) {
            const size = parseInt(sizeMatch[1], 10);
            if (size > effectiveFontSize) {
              effectiveFontSize = size;
            }
          }
        });
      }

      // Clean text from all formatting tags
      cleanText = text
        .replace(/<fontsize[^>]*>(.*?)<\/fontsize>/g, "$1")
        .replace(/<bold>(.*?)<\/bold>/g, "$1")
        .replace(/<italic>(.*?)<\/italic>/g, "$1")
        .replace(/<underline>(.*?)<\/underline>/g, "$1")
        .replace(/<highlight>(.*?)<\/highlight>/g, "$1")
        .replace(/<color[^>]*>(.*?)<\/color>/g, "$1");

      return { effectiveFontSize, cleanText };
    };

    const { effectiveFontSize, cleanText } =
      getEffectiveFontSizeAndCleanText(textContent);

    const tempText = new Konva.Text({
      text: cleanText,
      fontSize: effectiveFontSize,
      fontStyle: parsedFontStyle,
      fontFamily: "Roboto",
      width: textDimensions.width,
      align,
      wrap: "word",
    });

    setTextSize({
      width: tempText.width(),
      height: tempText.height(),
    });
  }, [textContent, fontSize, parsedFontStyle, align]);

  // Don't scale the text - let it render at its actual font size
  // Instead, we'll use CSS ellipsis to handle overflow
  const scale = 1; // Keep text at original size

  // Calculate stroke width accounting for zoom level
  const getEffectiveStrokeWidth = () => {
    return Math.max(
      1,
      (circle.strokeWidth || 2) / Math.max(0.3, effectiveZoomLevel || 1)
    );
  };

  /**
   * Note: for circle transforms we intentionally do NOT clamp radius by stage bounds.
   * Instead we clamp the node position on `onTransformEnd`, so a circle scaled out of
   * bounds "comes back in" via `x/y` while preserving the chosen radius.
   */

  /** Independent radii for ellipse: cap each axis to presets/stage fit. */
  const clampMainStageEllipseRadii = (rawRx, rawRy, node) => {
    const rxPreset = Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, rawRx));
    // CRITICAL FIX: For radiusY, use stage height constraint instead of MAX_RADIUS
    // This allows radiusY to extend up to the stage boundary (half stage height minus buffers)
    const ryPreset = Math.max(MIN_RADIUS, rawRy);
    
    if (!node?.getStage) {
      return { rx: rxPreset, ry: ryPreset };
    }
    
    // For off-canvas circles, apply proper clamping with transformer buffer
    if (isOffCanvas) {
      const stage = node.getStage();
      if (!stage) return { rx: rxPreset, ry: ryPreset };
      const sw = stage.width();
      const sh = stage.height();
      const strokeHalf = getEffectiveStrokeWidth() / 2;
      const transformerBuffer = TRANSFORMER_ANCHOR_BUFFER_PX;
      
      // Apply off-canvas specific clamping with transformer buffer
      const maxRx = Math.max(0, sw / 2 - strokeHalf - transformerBuffer);
      const maxRy = Math.max(0, sh / 2 - strokeHalf - transformerBuffer);
      
      const finalRx = Math.min(rxPreset, maxRx);
      const finalRy = Math.min(ryPreset, maxRy);
      
      // DEBUG: Log off-canvas clamping
      console.log("DEBUG: clampMainStageEllipseRadii off-canvas:", {
        rawRx: rawRx,
        rawRy: rawRy,
        rxPreset: rxPreset,
        ryPreset: ryPreset,
        stageWidth: sw,
        stageHeight: sh,
        strokeHalf: strokeHalf,
        transformerBuffer: transformerBuffer,
        maxRx: maxRx,
        maxRy: maxRy,
        finalRx: finalRx,
        finalRy: finalRy,
        MAX_RADIUS: MAX_RADIUS
      });
      
      return { rx: finalRx, ry: finalRy };
    }
    
    const stage = node.getStage();
    if (!stage) return { rx: rxPreset, ry: ryPreset };
    const sw = stage.width();
    const sh = stage.height();
    const strokeHalf = getEffectiveStrokeWidth() / 2;
    // CRITICAL FIX: Include transformer anchor buffer in radius calculations
    // The transformer anchors need space outside the shape, so we must account for TRANSFORMER_ANCHOR_BUFFER_PX
    const transformerBuffer = TRANSFORMER_ANCHOR_BUFFER_PX;
    
    // Clamp by global stage fit (diameter + stroke + transformer buffer), then translate x/y to fit.
    // This avoids unnecessary rotation when the ellipse can fit by repositioning.
    const maxRx = Math.max(0, sw / 2 - strokeHalf - transformerBuffer);
    const maxRy = Math.max(0, sh / 2 - strokeHalf - transformerBuffer);
    
    const finalRx = Math.min(rxPreset, maxRx);
    const finalRy = Math.min(ryPreset, maxRy);
    
    // DEBUG: Log detailed clamping calculation
    console.log("DEBUG: clampMainStageEllipseRadii detailed:", {
      rawRx: rawRx,
      rawRy: rawRy,
      rxPreset: rxPreset,
      ryPreset: ryPreset,
      stageWidth: sw,
      stageHeight: sh,
      strokeHalf: strokeHalf,
      transformerBuffer: transformerBuffer,
      maxRx: maxRx,
      maxRy: maxRy,
      finalRx: finalRx,
      finalRy: finalRy,
      MAX_RADIUS: MAX_RADIUS
    });
    
    return {
      rx: finalRx,
      ry: finalRy,
    };
  };

  const normalizeRotationDeg = (deg) => {
    let next = Number(deg) || 0;
    next %= 360;
    if (next < 0) next += 360;
    return next;
  };

  const getEffectiveInset = (requestedInset, stageSize, rectSize) => {
    const safeInset = Math.max(0, Number(requestedInset) || 0);
    const safeStage = Math.max(0, Number(stageSize) || 0);
    const safeRect = Math.max(0, Number(rectSize) || 0);
    return Math.max(0, Math.min(safeInset, (safeStage - safeRect) / 2));
  };

  const isClientRectInsideStage = (
    rect,
    stageWidth,
    stageHeight,
    inset = 0,
    eps = 0.5
  ) => {
    if (!rect) return true;
    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const insetY = getEffectiveInset(inset, stageHeight, rect.height);
    return (
      rect.x >= insetX - eps &&
      rect.y >= insetY - eps &&
      rect.x + rect.width <= stageWidth - insetX + eps &&
      rect.y + rect.height <= stageHeight - insetY + eps
    );
  };

  const clampNodePositionInsideStageByClientRect = (
    node,
    stageWidth,
    stageHeight,
    inset = 0
  ) => {
    if (!node) return { x: 0, y: 0 };
    const rect = node.getClientRect();
    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const insetY = getEffectiveInset(inset, stageHeight, rect.height);
    let dx = 0;
    let dy = 0;
    if (rect.x < insetX) dx = insetX - rect.x;
    else if (rect.x + rect.width > stageWidth - insetX) {
      dx = stageWidth - insetX - (rect.x + rect.width);
    }
    if (rect.y < insetY) dy = insetY - rect.y;
    else if (rect.y + rect.height > stageHeight - insetY) {
      dy = stageHeight - insetY - (rect.y + rect.height);
    }
    if (dx !== 0 || dy !== 0) {
      node.x((Number(node.x()) || 0) + dx);
      node.y((Number(node.y()) || 0) + dy);
    }
    return { x: node.x(), y: node.y() };
  };

  const clampRotationToStayWithinStage = (
    node,
    desiredRotation,
    stageWidth,
    stageHeight,
    inset = 0
  ) => {
    if (!node) return normalizeRotationDeg(desiredRotation);
    const base = normalizeRotationDeg(desiredRotation);
    const tryRotation = (candidate) => {
      node.rotation(candidate);
      return isClientRectInsideStage(
        node.getClientRect(),
        stageWidth,
        stageHeight,
        inset
      );
    };
    if (tryRotation(base)) return base;
    for (let step = 1; step <= 180; step += 1) {
      const left = normalizeRotationDeg(base - step);
      if (tryRotation(left)) return left;
      const right = normalizeRotationDeg(base + step);
      if (tryRotation(right)) return right;
    }
    node.rotation(base);
    return base;
  };

  const clampNodeInsideOffCanvasStripsByClientRect = (
    node,
    stageWidth,
    stageHeight,
    inset = 0
  ) => {
    if (!node || stageWidth <= 0 || stageHeight <= 0) {
      return { x: node?.x?.() ?? 0, y: node?.y?.() ?? 0 };
    }
    const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
    for (let pass = 0; pass < 8; pass += 1) {
      const rect = node.getClientRect();
      const insetX = getEffectiveInset(inset, stageWidth, rect.width);
      const insetY = getEffectiveInset(inset, stageHeight, rect.height);
      let dx = 0;
      let dy = 0;

      if (rect.x < insetX) dx += insetX - rect.x;
      else if (rect.x + rect.width > stageWidth - insetX) {
        dx += stageWidth - insetX - (rect.x + rect.width);
      }
      if (rect.y < insetY) dy += insetY - rect.y;
      else if (rect.y + rect.height > stageHeight - insetY) {
        dy += stageHeight - insetY - (rect.y + rect.height);
      }

      const overlapsMainColumn =
        rect.x < mainZoneEnd && rect.x + rect.width > mainZoneStart;
      if (overlapsMainColumn) {
        const centerX = rect.x + rect.width / 2;
        const mid = (mainZoneStart + mainZoneEnd) / 2;
        if (centerX < mid) {
          dx += mainZoneStart - (rect.x + rect.width);
        } else {
          dx += mainZoneEnd - rect.x;
        }
      }

      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) break;
      node.x((Number(node.x()) || 0) + dx);
      node.y((Number(node.y()) || 0) + dy);
    }
    return { x: node.x(), y: node.y() };
  };

  // When radii are changed via CircleEditor inputs (radius/radiusX/radiusY),
  // Konva doesn't call our onTransformEnd clamp paths.
  // This effect ensures the rendered circle/ellipse stays inside bounds (with
  // transformer buffer + off-canvas strip avoidance) immediately after radii updates.
  useEffect(() => {
    const node = shapeRef.current;
    if (!node) return;
    if (isPreview) return;
    // During live draw/clone, radius updates each pointer move; clamping here
    // forces the shape back inside stage and blocks out-of-bounds growth.
    if (circle?.isNew) return;
    if (transformStartCenterRef.current) return; // transform in progress

    const stage = node.getStage?.();
    if (!stage) return;

    const stageWidth = stage.width() || window.innerWidth * 0.45;
    const stageHeight = stage.height() || window.innerHeight;

    // First clamp to the outer stage bounds with the transformer inset buffer.
    clampNodePositionInsideStageByClientRect(
      node,
      stageWidth,
      stageHeight,
      TRANSFORMER_ANCHOR_BUFFER_PX,
    );

    // Then, if we're on off-canvas, ensure we don't overlap the main-column seam.
    if (isOffCanvas) {
      clampNodeInsideOffCanvasStripsByClientRect(
        node,
        stageWidth,
        stageHeight,
        TRANSFORMER_ANCHOR_BUFFER_PX,
      );
    }

    node.getLayer?.()?.batchDraw?.();
  }, [
    circle.radius,
    circle.radiusX,
    circle.radiusY,
    isOffCanvas,
    isPreview,
    maintainAspectRatio,
    effectiveZoomLevel,
  ]);

  // Calculate text container dimensions accounting for stroke width
  // Simplified approach similar to RectangleElement for consistent text handling
  const getTextContainerDimensions = () => {
    const strokeWidth = getEffectiveStrokeWidth();
    const strokePadding = Math.ceil(strokeWidth / 2); // Half stroke width on each side

    // Get current radius from the node if it exists (during transforms), otherwise use props
    let currentRadiusX = actualRadiusX;
    let currentRadiusY = actualRadiusY;

    if (shapeRef.current) {
      try {
        // Get scale factors (important during transforms)
        const scaleX = shapeRef.current.scaleX ? shapeRef.current.scaleX() : 1;
        const scaleY = shapeRef.current.scaleY ? shapeRef.current.scaleY() : 1;

        if (maintainAspectRatio) {
          // For circles, get the radius and apply scale
          const nodeRadius =
            typeof shapeRef.current.radius === "function"
              ? shapeRef.current.radius()
              : actualRadiusX;
          currentRadiusX = nodeRadius * scaleX;
          currentRadiusY = nodeRadius * scaleY;
        } else {
          // For ellipses, get radiusX and radiusY and apply scale
          const nodeRadiusX =
            typeof shapeRef.current.radiusX === "function"
              ? shapeRef.current.radiusX()
              : actualRadiusX;
          const nodeRadiusY =
            typeof shapeRef.current.radiusY === "function"
              ? shapeRef.current.radiusY()
              : actualRadiusY;
          currentRadiusX = nodeRadiusX * scaleX;
          currentRadiusY = nodeRadiusY * scaleY;
        }
      } catch (e) {
        // Fallback to props if there's an error reading from node
        currentRadiusX = actualRadiusX;
        currentRadiusY = actualRadiusY;
      }
    }

    // Calculate available space after accounting for stroke
    const availableWidth = Math.max(0, currentRadiusX * 2 - strokePadding * 2);
    const availableHeight = Math.max(0, currentRadiusY * 2 - strokePadding * 2);

    // Use the smaller dimension to maintain circular/oval shape
    const availableSize = Math.min(availableWidth, availableHeight);

    // Add extra padding to prevent text from merging with stroke
    // For circles, we need more padding than rectangles because text can extend at corners
    // Use a smaller percentage to create more space from stroke
    // Also add a minimum padding based on font size to handle large fonts
    const minPadding = Math.max(6, fontSize * 0.2); // At least 6px or 20% of font size for better clearance
    const paddingFromStroke = Math.max(minPadding, strokePadding * 1.5); // 1.5x stroke padding for extra clearance
    // Use 70% of available size and subtract additional padding to ensure text never touches stroke
    const containerSize = Math.max(0, availableSize * 0.7 - paddingFromStroke);

    return {
      size: containerSize,
      width: containerSize,
      height: containerSize,
      padding: strokePadding,
    };
  };

  // Don't render if completely outside canvas
  if (isOutsideCanvas()) {
    return null;
  }

  return (
    <>
      {maintainAspectRatio ? (
        <Circle
          ref={shapeRef}
          id={circle.id}
          radius={circle.radius}
          x={circle.x}
          y={circle.y}
          scaleX={1}
          scaleY={1}
          rotation={circle.rotation || 0}
          name={circle.name}
          stroke={applyTransparency(
            strokeColor || "#ffffff",
            circle.strokeOpacity ?? 1
          )}
          strokeWidth={getEffectiveStrokeWidth()}
          strokeScaleEnabled={false}
          strokeDashArray={isDrawing ? [5, 5] : null}
          strokeOpacity={circle.strokeOpacity !== undefined ? circle.strokeOpacity : 1}
          perfectDrawEnabled={true} // Enable perfect drawing for crisp edges
          shadowForStrokeEnabled={false} // Disable shadow for stroke to prevent fading
          fill={applyTransparency(
            circle.fill || circle.fillColor || "#ffffff",
            circle.fillOpacity ?? 1
          )}
          opacity={circle.opacity !== undefined ? circle.opacity : 1}
          listening={true}
          fillOpacity={circle.fillOpacity ?? 1}
          dragDistance={10}
          draggable={allowDrag}
          onDragMove={(e) => {
            // CRITICAL SAFETY CHECK: Only allow drag if mouse button is actually pressed
            if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
              const node = shapeRef.current;
              if (node) {
                node.stopDrag();
              }
              return;
            }

            const node = shapeRef.current;
            if (!node) return;

            // CRITICAL: If we just double-clicked, stop the drag immediately
            if (justDoubleClickedRef.current) {
              node.stopDrag();
              // Reset to original position
              if (doubleClickOriginalPositionRef.current.x !== null) {
                node.x(doubleClickOriginalPositionRef.current.x);
                node.y(doubleClickOriginalPositionRef.current.y);
              }
              // Disable dragging to prevent it from continuing
              node.setDraggable(false);
              // Re-enable after a delay
              setTimeout(() => {
                if (node && allowDrag) {
                  node.setDraggable(true);
                }
              }, 100);
              return;
            }

            let newX = node.x();
            let newY = node.y();

            // CRITICAL FIX: Update text Group position immediately during drag for smooth text movement
            // This prevents text lag by synchronizing text position with shape position in real-time
            if (textGroupRef.current) {
              const textDimensions = getTextContainerDimensions();
              textGroupRef.current.x(newX);
              textGroupRef.current.y(newY);
              textGroupRef.current.offsetX(textDimensions.size / 2);
              textGroupRef.current.offsetY(textDimensions.size / 2);

              // Force layer redraw for immediate visual update
              const layer = textGroupRef.current.getLayer();
              if (layer) {
                layer.batchDraw();
              }
            }

            onDragMove?.(
              {
                ...circle,
                id: circle.id,
                x: newX,
                y: newY,
                radius: circle.radius,
                text: circle.text,
              },
              e
            );
          }}
          onClick={(e) => {
            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }
            // Get the click position relative to the stage
            const stage = e.target.getStage();
            const pointerPosition = stage.getPointerPosition();

            // Use advanced hit detection to distinguish between stroke and fill areas
            const hitResult = checkAdvancedHitDetection(
              pointerPosition,
              { ...circle, type: "Circle", effectiveZoomLevel },
              images
            );

            if (
              hitResult.shouldSelectImage &&
              hitResult.selectedImage &&
              onImageClick
            ) {
              onImageClick(hitResult.selectedImage);
              return;
            }

            if (hitResult.shouldSelectShape && onClick) {
              // Select the circle
              e.evt?.stopPropagation?.(); // Prevent event bubbling
              e.evt?.preventDefault?.(); // Prevent default behavior

              // Mark this as a stroke click ONLY if the hit detection actually detected a stroke click
              // This prevents false positives when clicking on fill area
              if (e.target && e.target.attrs && hitResult.isOnStroke === true) {
                e.target.attrs.isStrokeClick = true;
              } else if (e.target && e.target.attrs) {
                // Clear the flag if it was previously set but this is not a stroke click
                e.target.attrs.isStrokeClick = false;
              }

              onClick(e);
              return;
            }

            // Fallback to default behavior
            if (onClick) {
              onClick(e);
            }
          }}
          onDblClick={(e) => {
            // Treat double-click as single click operation only
            // Prevent drag from starting after double-click
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();

            const node = shapeRef.current;
            if (node) {
              // Store original position to reset if drag starts
              doubleClickOriginalPositionRef.current = {
                x: node.x(),
                y: node.y()
              };

              // Immediately stop any ongoing drag
              node.stopDrag();

              // Disable dragging temporarily
              node.setDraggable(false);

              // Set flag to prevent drag from starting
              justDoubleClickedRef.current = true;

              // Re-enable dragging and clear flag after a delay
              setTimeout(() => {
                justDoubleClickedRef.current = false;
                doubleClickOriginalPositionRef.current = { x: null, y: null };
                if (node && allowDrag) {
                  node.setDraggable(true);
                }
              }, 500);
            }

            // Call onClick handler to treat double-click as single click
            if (onClick) {
              onClick(e);
            }
          }}
          onDragStart={(e) => {
            // CRITICAL SAFETY CHECK: Verify mouse button is actually pressed
            if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
              const node = e.target;
              if (node) {
                node.stopDrag();
              }
              return;
            }

            // Prevent drag from starting immediately after a double-click
            if (justDoubleClickedRef.current) {
              e.evt?.preventDefault?.();
              e.evt?.stopPropagation?.();
              const node = e.target;
              if (node) {
                // Stop the drag immediately
                node.stopDrag();
                // Reset position if we have it stored
                if (doubleClickOriginalPositionRef.current.x !== null) {
                  node.x(doubleClickOriginalPositionRef.current.x);
                  node.y(doubleClickOriginalPositionRef.current.y);
                }
                // Disable dragging
                node.setDraggable(false);
                // Re-enable after delay
                setTimeout(() => {
                  if (node && allowDrag) {
                    node.setDraggable(true);
                  }
                }, 100);
              }
              return;
            }

            // CRITICAL FIX: Initialize group drag immediately when drag starts if in multi-selection
            // This prevents the individual drag delay before group drag kicks in
            const node = e.target;
            const isInMultiSelection = selectedObjects && selectedObjects.length > 1 &&
              selectedObjects.some((obj) => obj.id === circle.id && obj.type === "Circle");

            if (isInMultiSelection && onDragMove) {
              // Immediately trigger group drag by calling onDragMove with current position
              // This ensures group drag starts immediately without individual element movement
              // CRITICAL: Pass the element attributes in the format expected by onDragMove
              const currentX = node.x();
              const currentY = node.y();
              
              onDragMove?.(
                {
                  ...circle,
                  id: circle.id,
                  x: currentX,
                  y: currentY,
                  radius: circle.radius,
                  text: circle.text,
                },
                e
              );
            }

            crossStageGestureBegin?.(
              typeof e.target?.getStage === "function"
                ? e.target.getStage()
                : null,
              e.evt,
            );
            if (isOffCanvas && e.target) {
              const st = e.target.getStage?.();
              const sw = st?.width?.() ?? 0;
              if (sw > 0) {
                const ext = getAbsoluteHorizontalExtent(
                  circle,
                  "Circle",
                  e.target.x(),
                );
                offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
                  ext.minX,
                  ext.maxX,
                  sw,
                );
              } else {
                offCanvasDragStripSideRef.current = null;
              }
            } else {
              offCanvasDragStripSideRef.current = null;
            }
          }}
          onDragEnd={(e) => {
            crossStageGestureEnd?.();
            const newX = Math.round(e.target.x());
            const newY = Math.round(e.target.y());
            // Get mouse coordinates from the event for group drag detection
            // Try multiple ways to get mouse coordinates as Konva event structure may vary
            const mouseClientX =
              e.evt?.clientX ??
              e.clientX ??
              e.evt?.changedTouches?.[0]?.clientX;
            const mouseClientY =
              e.evt?.clientY ??
              e.clientY ??
              e.evt?.changedTouches?.[0]?.clientY;


            offCanvasDragStripSideRef.current = null;
            onDragEnd(circle.id, newX, newY, mouseClientX, mouseClientY);
          }}
          onTransformStart={() => {
            crossStageGestureBegin?.(
              typeof shapeRef.current?.getStage === "function"
                ? shapeRef.current.getStage()
                : null,
              null,
            );
            if (shapeRef.current) {
              transformStartCenterRef.current = {
                x: shapeRef.current.x(),
                y: shapeRef.current.y(),
              };
            } else {
              transformStartCenterRef.current = null;
            }
            if (!isOffCanvas || !shapeRef.current) return;
            const node = shapeRef.current;
            const st = node.getStage?.();
            const sw = st?.width?.() ?? 0;
            if (sw <= 0) {
              offCanvasDragStripSideRef.current = null;
              return;
            }
            const nx = node.x();
            const merged = maintainAspectRatio
              ? { ...circle, x: nx, y: node.y(), radius: node.radius() }
              : {
                  ...circle,
                  x: nx,
                  y: node.y(),
                  radiusX: node.radiusX(),
                  radiusY: node.radiusY(),
                };
            const ext = getAbsoluteHorizontalExtent(merged, "Circle", nx);
            offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
              ext.minX,
              ext.maxX,
              sw,
            );
          }}
          onTransform={(e) => {
            const node = shapeRef.current;
            if (!node) return;
            const startCenter = transformStartCenterRef.current;
            if (startCenter) {
              // Keep circle center stable during transform so boundary clamp does
              // not drift the element toward stage bottom/right.
              node.x(startCenter.x);
              node.y(startCenter.y);
            }

            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            // Normalize rotation into 0–360 range
            let rotation = node.rotation();
            rotation = rotation % 360;
            if (rotation < 0) rotation += 360;

            // For circles, use uniform scaling based on scaleX
            const scale = scaleX;
            const nodeRadius = node.radius();
            const rawRadius = nodeRadius * scale;

            const finalX = Math.round(node.x());
            const finalY = Math.round(node.y());
            // IMPORTANT: do not clamp/snap radius during live transform.
            // Only clamp in `onTransformEnd` so the transformer anchor feels stable.
            const newRadius = Math.max(MIN_RADIUS, rawRadius);

            // Reset scale and apply computed radius so Konva node geometry stays in sync
            node.scaleX(1);
            node.scaleY(1);
            node.radius(newRadius);

            onTransform?.({
              ...circle,
              id: circle.id,
              x: finalX,
              y: finalY,
              rotation,
              radius: newRadius,
              text: circle.text,
            });
          }}
          onTransformEnd={(e) => {
            crossStageGestureEnd?.();
            const node = shapeRef.current;
            const startCenter = transformStartCenterRef.current;
            if (startCenter && node) {
              node.x(startCenter.x);
              node.y(startCenter.y);
            }
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            let rotation = Math.round(node.rotation());

            // Uniform scaling (Circle branch only; ovals use <Ellipse> below)
            const scale = scaleX;
            const nodeRadius = node.radius();
            const rawRadius = nodeRadius * scale;

            const finalX = Math.round(node.x());
            const finalY = Math.round(node.y());
            // Clamp radius to presets first.
            // If radius is larger than the stage can possibly contain (even with the
            // center clamped), clamp it further so the circle can actually fit.
            let newRadius = Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, rawRadius));
            if (!isOffCanvas) {
              const stage = node.getStage?.();
              const stageWidth = stage?.width() ?? window.innerWidth * 0.45;
              const stageHeight = stage?.height() ?? window.innerHeight;
              const strokeHalf = getEffectiveStrokeWidth() / 2;

              // Maximum radius so that (center clamp + stroke) can keep the full
              // circle inside the stage. If stage is too small for MIN_RADIUS,
              // we allow shrinking below MIN_RADIUS to avoid impossible bounds.
              const maxRadiusFit = Math.min(
                stageWidth / 2 - strokeHalf,
                stageHeight / 2 - strokeHalf,
              );
              const maxRadiusFitClamped = Math.max(0, maxRadiusFit);

              if (newRadius > maxRadiusFitClamped) {
                newRadius = maxRadiusFitClamped;
              }

              if (
                maxRadiusFitClamped >= MIN_RADIUS &&
                newRadius < MIN_RADIUS
              ) {
                newRadius = MIN_RADIUS;
              }
            }
            newRadius = Math.round(newRadius);

            node.scaleX(1);
            node.scaleY(1);
            node.radius(newRadius);

            let outX = finalX;
            let outY = finalY;
            const stage = node.getStage?.();
            if (stage) {
              const stageWidth = stage.width() || window.innerWidth * 0.45;
              const stageHeight = stage.height() || window.innerHeight;
              rotation = clampRotationToStayWithinStage(
                node,
                rotation,
                stageWidth,
                stageHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );
              // Clamp by center math (match MyCanvas) so transformerEdgeInset
              // is applied consistently to the final x/y.
              const radius = node.radius();
              const strokeHalf = getEffectiveStrokeWidth() / 2;
              const transformerEdgeInset = TRANSFORMER_ANCHOR_BUFFER_PX;

              const maxInsetX = Math.max(
                0,
                (stageWidth - radius * 2 - strokeHalf * 2) / 2,
              );
              const maxInsetY = Math.max(
                0,
                (stageHeight - radius * 2 - strokeHalf * 2) / 2,
              );

              const edgeInsetX = Math.min(transformerEdgeInset, maxInsetX);
              const edgeInsetY = Math.min(transformerEdgeInset, maxInsetY);

              const minX = radius + strokeHalf + edgeInsetX;
              const maxX = stageWidth - radius - strokeHalf - edgeInsetX;
              const minY = radius + strokeHalf + edgeInsetY;
              const maxY = stageHeight - radius - strokeHalf - edgeInsetY;

              const clampedX = Math.max(minX, Math.min(node.x(), maxX));
              const clampedY = Math.max(minY, Math.min(node.y(), maxY));

              node.x(clampedX);
              node.y(clampedY);

              outX = clampedX;
              outY = clampedY;
            }
            if (isOffCanvas) {
              if (stage) {
                const merged = {
                  ...circle,
                  x: finalX,
                  y: finalY,
                  radius: newRadius,
                };
                const pos = constrainOffCanvasPosition(
                  merged,
                  "Circle",
                  finalX,
                  finalY,
                  stage,
                  offCanvasDragStripSideRef.current,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                );
                outX = pos.constrainedX;
                outY = pos.constrainedY;
                node.x(outX);
                node.y(outY);
                const stripClamped = clampNodeInsideOffCanvasStripsByClientRect(
                  node,
                  stage.width() || window.innerWidth,
                  stage.height() || window.innerHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                );
                outX = stripClamped.x;
                outY = stripClamped.y;
              }
            }

            onTransformEnd?.({
              ...circle,
              id: circle.id,
              x: outX,
              y: outY,
              rotation: rotation,
              radius: newRadius,
              text: circle.text,
            });
            offCanvasDragStripSideRef.current = null;
            transformStartCenterRef.current = null;
          }}
        />
      ) : (
        <Ellipse
          ref={shapeRef}
          id={circle.id}
          radiusX={actualRadiusX}
          radiusY={actualRadiusY}
          x={circle.x}
          y={circle.y}
          scaleX={1}
          scaleY={1}
          rotation={circle.rotation || 0}
          name={circle.name}
          stroke={applyTransparency(
            strokeColor || "#ffffff",
            circle.strokeOpacity ?? 1
          )}
          strokeWidth={getEffectiveStrokeWidth()}
          strokeScaleEnabled={false}
          strokeDashArray={isDrawing ? [5, 5] : null}
          strokeOpacity={circle.strokeOpacity !== undefined ? circle.strokeOpacity : 1}
          perfectDrawEnabled={true} // Enable perfect drawing for crisp edges
          shadowForStrokeEnabled={false} // Disable shadow for stroke to prevent fading
          fill={applyTransparency(
            circle.fill || circle.fillColor || "#ffffff",
            circle.fillOpacity ?? 1
          )}
          opacity={circle.opacity !== undefined ? circle.opacity : 1}
          listening={true}
          fillOpacity={circle.fillOpacity ?? 1}
          dragDistance={10}
          draggable={allowDrag}
          onDragMove={(e) => {
            // CRITICAL SAFETY CHECK: Only allow drag if mouse button is actually pressed
            if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
              const node = shapeRef.current;
              if (node) {
                node.stopDrag();
              }
              return;
            }

            const node = shapeRef.current;
            if (!node) return;

            // CRITICAL: If we just double-clicked, stop the drag immediately
            if (justDoubleClickedRef.current) {
              node.stopDrag();
              // Reset to original position
              if (doubleClickOriginalPositionRef.current.x !== null) {
                node.x(doubleClickOriginalPositionRef.current.x);
                node.y(doubleClickOriginalPositionRef.current.y);
              }
              // Disable dragging to prevent it from continuing
              node.setDraggable(false);
              // Re-enable after a delay
              setTimeout(() => {
                if (node && allowDrag) {
                  node.setDraggable(true);
                }
              }, 100);
              return;
            }

            let newX = node.x();
            let newY = node.y();

            // CRITICAL FIX: Update text Group position immediately during drag for smooth text movement
            // This prevents text lag by synchronizing text position with shape position in real-time
            if (textGroupRef.current) {
              const textDimensions = getTextContainerDimensions();
              textGroupRef.current.x(newX);
              textGroupRef.current.y(newY);
              textGroupRef.current.offsetX(textDimensions.size / 2);
              textGroupRef.current.offsetY(textDimensions.size / 2);

              // Force layer redraw for immediate visual update
              const layer = textGroupRef.current.getLayer();
              if (layer) {
                layer.batchDraw();
              }
            }

            onDragMove?.(
              {
                ...circle,
                id: circle.id,
                x: newX,
                y: newY,
                radiusX: circle.radiusX || actualRadiusX,
                radiusY: circle.radiusY || actualRadiusY,
                text: circle.text,
              },
              e
            );
          }}
          onClick={(e) => {
            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }
            e.target.attrs.type = "Circle";

            // Get the click position relative to the stage
            const stage = e.target.getStage();
            const pointerPosition = stage.getPointerPosition();

            // Use advanced hit detection to distinguish between stroke and fill areas
            const hitResult = checkAdvancedHitDetection(
              pointerPosition,
              { ...circle, type: "Circle", effectiveZoomLevel },
              images
            );

            if (
              hitResult.shouldSelectImage &&
              hitResult.selectedImage &&
              onImageClick
            ) {
              onImageClick(hitResult.selectedImage);
              return;
            }

            if (hitResult.shouldSelectShape && onClick) {
              // Select the circle
              onClick(e);
              return;
            }

            // Fallback to default behavior
            if (onClick) {
              onClick(e);
            }
          }}
          onDblClick={(e) => {
            // Treat double-click as single click operation only
            // Prevent drag from starting after double-click
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();

            const node = shapeRef.current;
            if (node) {
              // Store original position to reset if drag starts
              doubleClickOriginalPositionRef.current = {
                x: node.x(),
                y: node.y()
              };

              // Immediately stop any ongoing drag
              node.stopDrag();

              // Disable dragging temporarily
              node.setDraggable(false);

              // Set flag to prevent drag from starting
              justDoubleClickedRef.current = true;

              // Re-enable dragging and clear flag after a delay
              setTimeout(() => {
                justDoubleClickedRef.current = false;
                doubleClickOriginalPositionRef.current = { x: null, y: null };
                if (node && allowDrag) {
                  node.setDraggable(true);
                }
              }, 500);
            }

            // Call onClick handler to treat double-click as single click
            if (onClick) {
              onClick(e);
            }
          }}
          onDragStart={(e) => {
            // CRITICAL SAFETY CHECK: Verify mouse button is actually pressed
            if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
              const node = e.target;
              if (node) {
                node.stopDrag();
              }
              return;
            }

            // Prevent drag from starting immediately after a double-click
            if (justDoubleClickedRef.current) {
              e.evt?.preventDefault?.();
              e.evt?.stopPropagation?.();
              const node = e.target;
              if (node) {
                // Stop the drag immediately
                node.stopDrag();
                // Reset position if we have it stored
                if (doubleClickOriginalPositionRef.current.x !== null) {
                  node.x(doubleClickOriginalPositionRef.current.x);
                  node.y(doubleClickOriginalPositionRef.current.y);
                }
                // Disable dragging
                node.setDraggable(false);
                // Re-enable after delay
                setTimeout(() => {
                  if (node && allowDrag) {
                    node.setDraggable(true);
                  }
                }, 100);
              }
              return;
            }

            // CRITICAL FIX: Initialize group drag immediately when drag starts if in multi-selection
            // This prevents the individual drag delay before group drag kicks in
            const node = e.target;
            const isInMultiSelection = selectedObjects && selectedObjects.length > 1 &&
              selectedObjects.some((obj) => obj.id === circle.id && obj.type === "Circle");

            if (isInMultiSelection && onDragMove) {
              // Immediately trigger group drag by calling onDragMove with current position
              // This ensures group drag starts immediately without individual element movement
              // CRITICAL: Pass the element attributes in the format expected by onDragMove
              const currentX = node.x();
              const currentY = node.y();
              
              onDragMove?.(
                {
                  ...circle,
                  id: circle.id,
                  x: currentX,
                  y: currentY,
                  radiusX: circle.radiusX || actualRadiusX,
                  radiusY: circle.radiusY || actualRadiusY,
                  text: circle.text,
                },
                e
              );
            }

            crossStageGestureBegin?.(
              typeof e.target?.getStage === "function"
                ? e.target.getStage()
                : null,
              e.evt,
            );
            if (isOffCanvas && e.target) {
              const st = e.target.getStage?.();
              const sw = st?.width?.() ?? 0;
              if (sw > 0) {
                const ext = getAbsoluteHorizontalExtent(
                  circle,
                  "Circle",
                  e.target.x(),
                );
                offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
                  ext.minX,
                  ext.maxX,
                  sw,
                );
              } else {
                offCanvasDragStripSideRef.current = null;
              }
            } else {
              offCanvasDragStripSideRef.current = null;
            }
          }}
          onDragEnd={(e) => {
            crossStageGestureEnd?.();
            const newX = Math.round(e.target.x());
            const newY = Math.round(e.target.y());
            // Get mouse coordinates from the event for group drag detection
            // Try multiple ways to get mouse coordinates as Konva event structure may vary
            const mouseClientX =
              e.evt?.clientX ??
              e.clientX ??
              e.evt?.changedTouches?.[0]?.clientX;
            const mouseClientY =
              e.evt?.clientY ??
              e.clientY ??
              e.evt?.changedTouches?.[0]?.clientY;


            offCanvasDragStripSideRef.current = null;
            onDragEnd(circle.id, newX, newY, mouseClientX, mouseClientY);
          }}
          onTransformStart={() => {
            crossStageGestureBegin?.(
              typeof shapeRef.current?.getStage === "function"
                ? shapeRef.current.getStage()
                : null,
              null,
            );
            if (shapeRef.current) {
              transformStartCenterRef.current = {
                x: shapeRef.current.x(),
                y: shapeRef.current.y(),
              };
            } else {
              transformStartCenterRef.current = null;
            }
            if (!isOffCanvas || !shapeRef.current) return;
            const node = shapeRef.current;
            const st = node.getStage?.();
            const sw = st?.width?.() ?? 0;
            if (sw <= 0) {
              offCanvasDragStripSideRef.current = null;
              return;
            }
            const nx = node.x();
            const merged = maintainAspectRatio
              ? { ...circle, x: nx, y: node.y(), radius: node.radius() }
              : {
                  ...circle,
                  x: nx,
                  y: node.y(),
                  radiusX: node.radiusX(),
                  radiusY: node.radiusY(),
                };
            const ext = getAbsoluteHorizontalExtent(merged, "Circle", nx);
            offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
              ext.minX,
              ext.maxX,
              sw,
            );
          }}
          onTransform={(e) => {
            const node = shapeRef.current;
            if (!node) return;
            const startCenter = transformStartCenterRef.current;
            if (startCenter) {
              // Keep ellipse center stable during transform so boundary clamp does
              // not drift the element toward stage bottom/right.
              node.x(startCenter.x);
              node.y(startCenter.y);
            }

            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            // Normalize rotation into 0–360 range
            let rotation = node.rotation();
            rotation = rotation % 360;
            if (rotation < 0) rotation += 360;

            // For ellipses (maintainAspectRatio=false), allow independent X/Y scaling
            const nodeRadiusX = node.radiusX();
            const nodeRadiusY = node.radiusY();

            const finalX = Math.round(node.x());
            const finalY = Math.round(node.y());
            // IMPORTANT: do not clamp/snap radii during live transform.
            // Only clamp in `onTransformEnd` to keep anchors stable.
            const newRadiusX = Math.max(MIN_RADIUS, nodeRadiusX * scaleX);
            const newRadiusY = Math.max(MIN_RADIUS, nodeRadiusY * scaleY);

            // Reset scale and apply computed radii so Konva node geometry stays in sync
            node.scaleX(1);
            node.scaleY(1);
            node.radiusX(newRadiusX);
            node.radiusY(newRadiusY);

            onTransform?.({
              ...circle,
              id: circle.id,
              x: finalX,
              y: finalY,
              rotation,
              radiusX: newRadiusX,
              radiusY: newRadiusY,
              text: circle.text,
            });
          }}
          onTransformEnd={(e) => {
            crossStageGestureEnd?.();
            const node = shapeRef.current;
            const startCenter = transformStartCenterRef.current;
            if (startCenter && node) {
              node.x(startCenter.x);
              node.y(startCenter.y);
            }
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            let rotation = Math.round(node.rotation());

            // For ovals with maintainAspectRatio=false, ALL anchors allow geometry changes
            const nodeRadiusX = node.radiusX();
            const nodeRadiusY = node.radiusY();

            // Get final position
            const finalX = Math.round(node.x());
            const finalY = Math.round(node.y());
            const { rx: newRadiusX, ry: newRadiusY } = (() => {
              const rawRx = nodeRadiusX * scaleX;
              const rawRy = nodeRadiusY * scaleY;
              const { rx, ry } = clampMainStageEllipseRadii(
                rawRx,
                rawRy,
                node,
              );
              
              // Get stage reference for debug logging
              const stage = node.getStage?.();
              
              // DEBUG: Log transform end clamping
              console.log("DEBUG: Circle onTransformEnd clamping:", {
                circleId: circle.id,
                isOffCanvas: isOffCanvas,
                maintainAspectRatio: maintainAspectRatio,
                rawRadiusX: rawRx,
                rawRadiusY: rawRy,
                clampedRadiusX: Math.round(rx),
                clampedRadiusY: Math.round(ry),
                stageWidth: stage?.width?.() || 'N/A',
                stageHeight: stage?.height?.() || 'N/A',
                MAX_RADIUS: MAX_RADIUS
              });
              
              return { rx: Math.round(rx), ry: Math.round(ry) };
            })();

            // Reset scale and update ellipse dimensions
            node.scaleX(1);
            node.scaleY(1);
            node.radiusX(newRadiusX);
            node.radiusY(newRadiusY);

            let outX = finalX;
            let outY = finalY;
            const stage = node.getStage?.();
            if (stage) {
              const stageWidth = stage.width() || window.innerWidth * 0.45;
              const stageHeight = stage.height() || window.innerHeight;
              const shifted = clampNodePositionInsideStageByClientRect(
                node,
                stageWidth,
                stageHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );
              outX = shifted.x;
              outY = shifted.y;
              if (
                !isClientRectInsideStage(
                  node.getClientRect(),
                  stageWidth,
                  stageHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                )
              ) {
                rotation = clampRotationToStayWithinStage(
                  node,
                  rotation,
                  stageWidth,
                  stageHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                );
                const shiftedAfterRotation = clampNodePositionInsideStageByClientRect(
                  node,
                  stageWidth,
                  stageHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                );
                outX = shiftedAfterRotation.x;
                outY = shiftedAfterRotation.y;
              }
            }
            if (isOffCanvas) {
              if (stage) {
                const merged = {
                  ...circle,
                  x: finalX,
                  y: finalY,
                  radiusX: newRadiusX,
                  radiusY: newRadiusY,
                };
                const pos = constrainOffCanvasPosition(
                  merged,
                  "Circle",
                  finalX,
                  finalY,
                  stage,
                  offCanvasDragStripSideRef.current,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                );
                outX = pos.constrainedX;
                outY = pos.constrainedY;
                node.x(outX);
                node.y(outY);
                const stripClamped = clampNodeInsideOffCanvasStripsByClientRect(
                  node,
                  stage.width() || window.innerWidth,
                  stage.height() || window.innerHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                );
                outX = stripClamped.x;
                outY = stripClamped.y;
              }
            }

            onTransformEnd?.({
              ...circle,
              id: circle.id,
              x: outX,
              y: outY,
              rotation: rotation,
              radiusX: newRadiusX,
              radiusY: newRadiusY,
              text: circle.text,
            });
            offCanvasDragStripSideRef.current = null;
            transformStartCenterRef.current = null;
          }}
        />
      )}

      {textContent &&
        (() => {
          const textDimensions = getTextContainerDimensions();

          // CRITICAL FIX: Always read position from shape ref for smooth text movement during drag
          // The ref gives us the real-time position, while props only update on React re-render
          // This prevents text lag during drag operations
          let currentX = circle.x;
          let currentY = circle.y;
          let currentRotation = circle.rotation || 0;

          // Check if this element is part of a group drag (multiple selected objects)
          const isInGroupDrag = isInGroupDragRef.current;

          // CRITICAL PERFORMANCE FIX: Use cached HTML content during group drag to prevent expensive re-renders
          // During group drag, only Konva node positions are updated via requestAnimationFrame,
          // not the HTML content. This prevents lag when dragging elements with large HTML content.
          const htmlContentToRender = isInGroupDrag
            ? htmlContentRef.current
            : (textContent || "");

          // CRITICAL FIX: Always use ref position if available for smooth text movement
          // This ensures text follows the shape instantly during drag, not just during group drag
          if (shapeRef.current) {
            try {
              // Always read from ref to get real-time position during any interaction
              // This prevents text lag during single element drag
              currentX = shapeRef.current.x();
              currentY = shapeRef.current.y();
              currentRotation = shapeRef.current.rotation();
            } catch (e) {
              // Fallback to props if ref access fails
              currentX = circle.x;
              currentY = circle.y;
              currentRotation = circle.rotation || 0;
            }
          }

          return (
            <Group
              ref={textGroupRef}
              key={`text-group-${circle.id}-${renderKey}-${circle.x}-${circle.y}`}
              x={currentX}
              y={currentY}
              offsetX={textDimensions.size / 2}
              offsetY={textDimensions.size / 2}
              rotation={currentRotation}
              scaleX={1}
              scaleY={1}
              listening={false}
              clipFunc={(ctx) => {
                ctx.beginPath();

                // Canvas boundaries (use effective height to account for resize handle)
                const canvasLeft = 0;
                const canvasTop = 0;
                const canvasRight = canvasPixelWidth;
                const canvasBottom = effectiveCanvasHeight;

                // Calculate the visible area of this circle/oval element using text container dimensions
                const elementLeft = currentX - textDimensions.size / 2;
                const elementTop = currentY - textDimensions.size / 2;
                const elementRight = currentX + textDimensions.size / 2;
                const elementBottom = currentY + textDimensions.size / 2;

                // Calculate the intersection with canvas boundaries
                const clipLeft = Math.max(canvasLeft, elementLeft);
                const clipTop = Math.max(canvasTop, elementTop);
                const clipRight = Math.min(canvasRight, elementRight);
                const clipBottom = Math.min(canvasBottom, elementBottom);

                // If element is completely outside canvas, don't render it
                if (clipLeft >= clipRight || clipTop >= clipBottom) {
                  ctx.rect(0, 0, 0, 0); // Empty clip
                  ctx.closePath();
                  return;
                }

                // Clip to the circle/ellipse shape using the container size
                // The container size already accounts for padding from stroke
                const radiusForClip = textDimensions.size / 2;
                ctx.ellipse(
                  0,
                  0,
                  radiusForClip,
                  radiusForClip,
                  0,
                  0,
                  2 * Math.PI
                );
                ctx.closePath();
                ctx.clip();

                // Now apply canvas boundary clipping in local coordinates
                // Convert to local coordinates for the Group
                const localClipLeft = clipLeft - elementLeft;
                const localClipTop = clipTop - elementTop;
                const localClipWidth = clipRight - clipLeft;
                const localClipHeight = clipBottom - clipTop;

                // Apply second clipping for canvas boundaries
                ctx.beginPath();
                ctx.rect(
                  localClipLeft,
                  localClipTop,
                  localClipWidth,
                  localClipHeight
                );
                ctx.closePath();
              }}
            >
              {/* Render text with HTML formatting */}
              <Html
                divProps={{
                  "data-circle-id": circle.id,
                  "data-behind-selected": isHtmlContentBehindSelected()
                    ? "true"
                    : "false",
                  style: {
                    pointerEvents: "none",
                    zIndex: isSelected
                      ? 15
                      : isHtmlContentBehindSelected()
                        ? 1
                        : 3, // Selected elements on top (15), behind selected (1), non-selected below (3)
                    position: "absolute", // Ensure z-index works
                    ...(() => {
                      // Calculate actual div dimensions (same as in div style)
                      const circleLeft = currentX - textDimensions.size / 2;
                      const availableWidth = canvasPixelWidth - circleLeft;
                      const actualDivWidth = Math.max(
                        0,
                        Math.min(textDimensions.size, availableWidth)
                      );

                      const circleTop = currentY - textDimensions.size / 2;
                      const circleBottom = circleTop + textDimensions.size;
                      let actualDivHeight = 0;

                      if (
                        !(
                          circleTop >= effectiveCanvasHeight ||
                          circleBottom <= 0
                        )
                      ) {
                        const visibleTop = Math.max(0, circleTop);
                        const visibleBottom = Math.min(
                          effectiveCanvasHeight,
                          circleBottom
                        );
                        const visibleHeight = visibleBottom - visibleTop;
                        actualDivHeight = Math.max(
                          0,
                          Math.min(visibleHeight, textDimensions.size)
                        );
                      } else {
                        actualDivHeight = textDimensions.size;
                      }

                      // Get mask styles
                      const maskStyles = getHtmlMask(
                        actualDivWidth,
                        actualDivHeight
                      );

                      // CRITICAL: Add clipping for canvas boundaries (only for main canvas)
                      if (!isOffCanvas) {
                        const circleRight = circleLeft + textDimensions.size;
                        const circleBottom = circleTop + textDimensions.size;

                        // Calculate overflow using full text element bounds (like TextElement)
                        const overflowLeft = Math.max(0, -circleLeft);
                        const overflowTop = Math.max(0, -circleTop);
                        const overflowRight = Math.max(
                          0,
                          circleRight - canvasPixelWidth
                        );
                        const overflowBottom = Math.max(
                          0,
                          circleBottom - effectiveCanvasHeight
                        );

                        // If any overflow exists, apply clipping
                        if (
                          overflowLeft > 0 ||
                          overflowTop > 0 ||
                          overflowRight > 0 ||
                          overflowBottom > 0
                        ) {
                          // Use full width/height - let clip-path handle clipping (like TextElement)
                          // clip-path inset() takes: top, right, bottom, left (distances from edges)
                          return {
                            ...maskStyles,
                            overflow: "hidden",
                            clipPath: `inset(${overflowTop}px ${overflowRight}px ${overflowBottom}px ${overflowLeft}px)`,
                            width: `${textDimensions.size}px`, // Use full width
                            height: `${textDimensions.size}px`, // Use full height
                          };
                        }
                      }

                      // CRITICAL: Explicitly remove clipping when element is fully inside canvas
                      return {
                        ...maskStyles,
                        overflow: "visible",
                        clipPath: "none",
                        width: `${textDimensions.size}px`, // Use full width
                        height: `${textDimensions.size}px`, // Use full height
                      };
                    })(), // Use mask to hide overlapping portions when behind selected elements
                  },
                }}
              >
                {(() => {
                  // Use full dimensions - let container clip-path handle clipping (like TextElement)
                  return (
                    <div
                      style={{
                        width: `${textDimensions.size}px`, // Use full width
                        height: `${textDimensions.size}px`, // Use full height
                        clipPath: "none",
                        WebkitClipPath: "none",
                        display: "flex",
                        alignItems:
                          verticalAlign === "top"
                            ? "flex-start"
                            : verticalAlign === "bottom"
                              ? "flex-end"
                              : "center",
                        justifyContent:
                          align === "left"
                            ? "flex-start"
                            : align === "right"
                              ? "flex-end"
                              : "center",
                        padding: "0px",
                        margin: "0px",
                        overflow: "hidden",
                        pointerEvents: "none",
                        cursor: "default",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        MozUserSelect: "none",
                        msUserSelect: "none",
                        visibility: (() => {
                          const circleTop = currentY - textDimensions.size / 2;
                          const circleBottom = circleTop + textDimensions.size;
                          if (
                            circleTop >= effectiveCanvasHeight ||
                            circleBottom <= 0
                          ) {
                            return "hidden";
                          }
                          return "visible";
                        })(),
                      }}
                    >
                      <div
                        className="circle-text-content"
                        data-export="true"
                        data-content-type="circle"
                        style={{
                          fontFamily: "Roboto",
                          fontSize: `${fontSize}px`,
                          fontWeight: fontStyle.includes("bold")
                            ? "bold"
                            : "normal",
                          fontStyle: fontStyle.includes("italic")
                            ? "italic"
                            : "normal",
                          textDecoration: textDecoration,
                          color: textColor,
                          textAlign: align,
                          lineHeight: "1em",
                          wordWrap: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: Math.max(
                            1,
                            Math.floor(textDimensions.height / fontSize)
                          ),
                          WebkitBoxOrient: "vertical",
                          whiteSpace: "pre-wrap",
                          maxWidth: `${textDimensions.width}px`,
                          maxHeight: `${textDimensions.height}px`,
                          width: `${textDimensions.width}px`,
                          margin: "0",
                          padding: "0",
                          boxSizing: "border-box",
                          pointerEvents: "none",
                          userSelect: "none",
                          opacity: getHtmlOpacity(),
                        }}
                        dangerouslySetInnerHTML={{
                          __html: htmlContentToRender,
                        }}
                      />
                    </div>
                  );
                })()}
              </Html>
            </Group>
          );
        })()}

      {/* Copy button for off-canvas circles */}
      {(() => {
        const radius =
          circle.radius || Math.max(circle.radiusX || 0, circle.radiusY || 0);
        // Copy button commented out
        const shouldShowCopyButton =
          false &&
          isSelected &&
          !isPreview &&
          !isDrawing &&
          isOffCanvas &&
          typeof onCopyToOppositeCanvas === "function";

        if (!shouldShowCopyButton) return null;

        const circleX = circle.x ?? 0;
        const circleY = circle.y ?? 0;
        const copyButtonLeft = Math.max(
          4,
          Math.min(circleX + radius - 14, canvasPixelWidth - 28)
        );
        const copyButtonTop = Math.max(
          4,
          Math.min(circleY - radius - 36, effectiveCanvasHeight - 28)
        );

        return (
          <Html>
            <div
              style={{
                position: "absolute",
                top: `${copyButtonTop}px`,
                left: `${copyButtonLeft}px`,
                pointerEvents: "none",
                zIndex: 30,
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyToOppositeCanvas?.();
                }}
                title="Copy to Main Canvas"
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "4px",
                  border: "1px solid #CBD5E1",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  padding: 0,
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                  e.currentTarget.style.borderColor = "#94A3B8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#CBD5E1";
                }}
              >
                <svg
                  width="16"
                  height="19"
                  viewBox="0 0 17 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                    fill="#1E293B"
                  />
                </svg>
              </button>
            </div>
          </Html>
        );
      })()}
    </>
  );
};

export default CircleElement;
